import React, { useState, useEffect, useCallback } from "react";
import { Toast, ToastContainer } from "react-bootstrap";

/**
 * Toast Notification System
 * Accessibility: ARIA live regions, keyboard dismissable
 * Auto-dismiss with configurable timeout
 */

let toastIdCounter = 0;
const toastListeners = new Set();

export const showToast = (message, variant = "info", duration = 3000) => {
  const toast = {
    id: ++toastIdCounter,
    message,
    variant, // 'success', 'danger', 'warning', 'info'
    duration,
    timestamp: Date.now(),
  };

  toastListeners.forEach((listener) => listener(toast));
  return toast.id;
};

const ToastNotification = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    setToasts((prev) => [...prev, toast]);

    // Auto-dismiss
    if (toast.duration > 0) {
      setTimeout(() => {
        removeToast(toast.id);
      }, toast.duration);
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    toastListeners.add(addToast);
    return () => toastListeners.delete(addToast);
  }, [addToast]);

  const getIcon = (variant) => {
    switch (variant) {
      case "success":
        return "bi-check-circle-fill";
      case "danger":
        return "bi-x-circle-fill";
      case "warning":
        return "bi-exclamation-triangle-fill";
      case "info":
      default:
        return "bi-info-circle-fill";
    }
  };

  return (
    <ToastContainer
      position="top-end"
      className="p-3"
      style={{ zIndex: 9999 }}
      role="region"
      aria-label="Thông báo"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          onClose={() => removeToast(toast.id)}
          show={true}
          delay={toast.duration}
          autohide={toast.duration > 0}
          bg={toast.variant}
          className="fade-in"
        >
          <Toast.Header>
            <i className={`bi ${getIcon(toast.variant)} me-2`}></i>
            <strong className="me-auto">
              {toast.variant === "success" && "Thành công"}
              {toast.variant === "danger" && "Lỗi"}
              {toast.variant === "warning" && "Cảnh báo"}
              {toast.variant === "info" && "Thông báo"}
            </strong>
          </Toast.Header>
          <Toast.Body
            className={
              toast.variant === "danger" || toast.variant === "warning"
                ? "text-white"
                : ""
            }
          >
            {toast.message}
          </Toast.Body>
        </Toast>
      ))}
    </ToastContainer>
  );
};

export default ToastNotification;
